/**
 * ParkingSpotsList Component
 *
 * Displays and manages spots within a parking zone.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  Box,
  Typography,
  Button,
  Stack,
  Chip,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  DialogContent,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import {
  useParkingZone,
  useCreateParkingSpot,
  useBatchCreateSpots,
  useUpdateParkingSpot,
  useDeleteParkingSpot,
  isParkingSpotOccupiedError,
  type ParkingSpot,
} from '@/hooks/useParking';
import { useNotify } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { FormDialog } from '@/components/common/FormDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';

interface ParkingSpotsListProps {
  zoneId: string;
}

export function ParkingSpotsList({ zoneId }: ParkingSpotsListProps) {
  const { t } = useTranslation('equipment');
  const { t: tc } = useTranslation('common');
  const hasPermission = useAuthStore((s) => s.hasPermission);
  // Spot mutations require equipment.manage server-side; hide what would 403.
  const canManageEquipment = hasPermission('equipment.manage');
  const notify = useNotify();

  const { data: zone, isLoading } = useParkingZone(zoneId);
  const createSpotMutation = useCreateParkingSpot();
  const batchCreateMutation = useBatchCreateSpots();
  const updateSpotMutation = useUpdateParkingSpot();
  const deleteSpotMutation = useDeleteParkingSpot();

  const [showAddSingle, setShowAddSingle] = useState(false);
  const [showAddBatch, setShowAddBatch] = useState(false);
  const [singleIdentifier, setSingleIdentifier] = useState('');
  const [batchPrefix, setBatchPrefix] = useState('');
  const [batchStart, setBatchStart] = useState(1);
  const [batchCount, setBatchCount] = useState(5);
  const [editingSpot, setEditingSpot] = useState<ParkingSpot | null>(null);
  const [editIdentifier, setEditIdentifier] = useState('');
  const [editDirty, setEditDirty] = useState(false);
  const [deleteSpot, setDeleteSpot] = useState<ParkingSpot | null>(null);

  const handleAddSingle = async () => {
    if (!singleIdentifier.trim()) return;
    try {
      await createSpotMutation.mutateAsync({
        zoneId,
        data: { identifier: singleIdentifier.trim() },
      });
      notify.success(t('parking.spotCreated'));
      setSingleIdentifier('');
      setShowAddSingle(false);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('parking.saveFailed') }));
    }
  };

  const handleAddBatch = async () => {
    if (!batchPrefix.trim() || batchCount < 1) return;
    try {
      await batchCreateMutation.mutateAsync({
        zoneId,
        data: {
          prefix: batchPrefix.trim(),
          startNumber: batchStart,
          count: batchCount,
        },
      });
      notify.success(t('parking.spotsCreated', { count: batchCount }));
      setBatchPrefix('');
      setBatchStart(1);
      setBatchCount(5);
      setShowAddBatch(false);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('parking.saveFailed') }));
    }
  };

  const handleOpenEdit = (spot: ParkingSpot) => {
    setEditingSpot(spot);
    setEditIdentifier(spot.identifier);
    setEditDirty(false);
  };

  const handleCloseEdit = () => {
    setEditingSpot(null);
    setEditIdentifier('');
    setEditDirty(false);
  };

  const handleUpdateSpot = async () => {
    if (!editingSpot || !editIdentifier.trim()) return;
    try {
      await updateSpotMutation.mutateAsync({
        id: editingSpot.id,
        data: { identifier: editIdentifier.trim() },
      });
      notify.success(t('parking.spotUpdated'));
      handleCloseEdit();
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('parking.saveFailed') }));
    }
  };

  const handleDeleteSpot = async () => {
    if (!deleteSpot) return;
    try {
      await deleteSpotMutation.mutateAsync(deleteSpot.id);
      notify.success(t('parking.spotDeleted'));
    } catch (error) {
      notify.error(
        tc('error'),
        isParkingSpotOccupiedError(error)
          ? t('parking.spotOccupiedCannotDelete')
          : t('parking.deleteFailed'),
      );
    }
    setDeleteSpot(null);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const spots = zone?.spots ?? [];

  return (
    <Box>
      <Divider sx={{ mb: 2 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle2">
          {t('parking.spots')} ({spots.length})
        </Typography>
        {canManageEquipment && (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => { setShowAddSingle(true); setShowAddBatch(false); }}
            >
              {t('parking.addSpot')}
            </Button>
            <Button
              size="small"
              startIcon={<PlaylistAddIcon />}
              onClick={() => { setShowAddBatch(true); setShowAddSingle(false); }}
            >
              {t('parking.batchAddSpots')}
            </Button>
          </Stack>
        )}
      </Stack>

      {showAddSingle && (
        <Stack direction="row" spacing={1} mb={2} alignItems="flex-start">
          <TextField
            size="small"
            label={t('parking.spotIdentifier')}
            placeholder={t('parking.spotIdentifierPlaceholder')}
            value={singleIdentifier}
            onChange={(e) => setSingleIdentifier(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void handleAddSingle(); }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleAddSingle}
            disabled={createSpotMutation.isPending}
            sx={{ mt: '4px !important' }}
          >
            {tc('add')}
          </Button>
          <Button
            size="small"
            onClick={() => setShowAddSingle(false)}
            sx={{ mt: '4px !important' }}
          >
            {tc('cancel')}
          </Button>
        </Stack>
      )}

      {showAddBatch && (
        <Stack direction="row" spacing={1} mb={2} alignItems="flex-start" flexWrap="wrap">
          <TextField
            size="small"
            label={t('parking.prefix')}
            placeholder={t('parking.prefixPlaceholder')}
            value={batchPrefix}
            onChange={(e) => setBatchPrefix(e.target.value)}
            sx={{ width: 120 }}
          />
          <TextField
            size="small"
            label={t('parking.startNumber')}
            type="number"
            value={batchStart}
            onChange={(e) => setBatchStart(parseInt(e.target.value) || 1)}
            sx={{ width: 100 }}
          />
          <TextField
            size="small"
            label={t('parking.count')}
            type="number"
            value={batchCount}
            onChange={(e) => setBatchCount(parseInt(e.target.value) || 1)}
            sx={{ width: 100 }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleAddBatch}
            disabled={batchCreateMutation.isPending}
            sx={{ mt: '4px !important' }}
          >
            {tc('add')}
          </Button>
          <Button
            size="small"
            onClick={() => setShowAddBatch(false)}
            sx={{ mt: '4px !important' }}
          >
            {tc('cancel')}
          </Button>
        </Stack>
      )}

      {spots.length === 0 ? (
        <Alert severity="info" variant="outlined">
          <Typography variant="body2">{t('parking.noSpotsDesc')}</Typography>
        </Alert>
      ) : (
        <Stack spacing={0.5}>
          {spots.map((spot) => (
            <Stack
              key={spot.id}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              sx={{ py: 0.25 }}
            >
              <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                  {spot.identifier}
                </Typography>
                <Chip
                  size="small"
                  label={spot.isAvailable ? t('parking.available') : t('parking.occupied')}
                  color={spot.isAvailable ? 'success' : 'default'}
                  variant={spot.isAvailable ? 'outlined' : 'filled'}
                />
              </Stack>
              {canManageEquipment && (
                <Stack direction="row" spacing={0.5} flexShrink={0}>
                  <Tooltip title={tc('edit')}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEdit(spot)}
                      aria-label={tc('edit')}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={spot.isAvailable ? '' : t('parking.spotOccupiedCannotDelete')}
                  >
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={!spot.isAvailable}
                        onClick={() => setDeleteSpot(spot)}
                        aria-label={tc('delete')}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              )}
            </Stack>
          ))}
        </Stack>
      )}

      <FormDialog
        open={!!editingSpot}
        onClose={handleCloseEdit}
        title={t('parking.editSpot')}
        isDirty={editDirty}
        onSubmit={() => void handleUpdateSpot()}
        isLoading={updateSpotMutation.isPending}
        submitLabel={tc('save')}
        submitDisabled={!editIdentifier.trim()}
        maxWidth="xs"
      >
        <DialogContent>
          <TextField
            label={t('parking.spotIdentifier')}
            value={editIdentifier}
            onChange={(e) => {
              setEditIdentifier(e.target.value);
              setEditDirty(true);
            }}
            placeholder={t('parking.spotIdentifierPlaceholder')}
            fullWidth
            required
            sx={{ mt: 1 }}
          />
        </DialogContent>
      </FormDialog>

      <ConfirmDialog
        open={!!deleteSpot}
        title={t('parking.deleteSpotTitle')}
        message={t('parking.deleteSpotConfirm', { identifier: deleteSpot?.identifier })}
        confirmColor="error"
        onConfirm={() => void handleDeleteSpot()}
        onCancel={() => setDeleteSpot(null)}
        loading={deleteSpotMutation.isPending}
      />
    </Box>
  );
}
